import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper to format date
const formatDate = () => {
    const now = new Date();
    return now.toLocaleString('nl-NL');
};

export const PdfService = {
    generatePermitPdf: (data) => {
        // Initialize jsPDF
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // --- HEADER ---
        doc.setFillColor(30, 41, 59); // Slate-800
        doc.rect(0, 0, pageWidth, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.text("Werkplan - Werkinstructie", 14, 25);

        doc.setFontSize(10);
        doc.text(`Gegenereerd: ${formatDate()}`, pageWidth - 14, 25, { align: 'right' });
        doc.text(`Ultimo: ${data.ultimoNr || 'N.v.t.'}`, pageWidth - 14, 32, { align: 'right' });

        let currentY = 50;

        // --- INFO BLOCKS ---
        doc.setTextColor(0, 0, 0);

        // Left Column (Asset)
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text("Locatie & Asset", 14, currentY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        currentY += 6;
        doc.text(`Locatie: ${data.location || '-'}`, 14, currentY);
        currentY += 5;
        doc.text(`Hal: ${data.hall || '-'}`, 14, currentY);
        currentY += 5;
        doc.text(`Lijn: ${data.line || '-'}`, 14, currentY);
        currentY += 5;
        doc.text(`Onderdeel: ${data.component || '-'}`, 14, currentY);

        // Right Column (Task)
        let rightColX = pageWidth / 2 + 10;
        let rightColY = 50;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text("Taak Details", rightColX, rightColY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        rightColY += 6;
        doc.text(`Taak: ${data.task || '-'}`, rightColX, rightColY);
        rightColY += 5;
        doc.text(`Uitvoerende: ${data.monteur1 || data.assignedTo || data.applicantName || '-'}`, rightColX, rightColY);
        rightColY += 5;
        doc.text(`Werkverantwoordelijke: ${data.werkverantwoordelijke || 'N.v.t.'}`, rightColX, rightColY);

        currentY = Math.max(currentY, rightColY) + 15;

        // --- PBMS (New Section) ---
        if (data.pbms && data.pbms.length > 0) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text("Benodigde PBM's", 14, currentY);
            currentY += 6;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');

            const pbmList = data.pbms.map(p => p.PBM || p).join(', ');
            const splitPbms = doc.splitTextToSize(pbmList, pageWidth - 28);
            doc.text(splitPbms, 14, currentY);

            currentY += (splitPbms.length * 5) + 5;
        }

        // --- TOOLS (New Section) ---
        if (data.tools && data.tools.length > 0) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text("Benodigde Gereedschappen", 14, currentY);
            currentY += 6;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');

            const toolList = data.tools.map(t => t.Tool || t).join(', ');
            const splitTools = doc.splitTextToSize(toolList, pageWidth - 28);
            doc.text(splitTools, 14, currentY);

            currentY += (splitTools.length * 5) + 10;
        }

        // --- RISKS & MEASURES ---
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text("Veiligheid (TRA)", 14, currentY);
        currentY += 5;

        const riskRows = (data.risks || []).map(r => [r.Risico, r.Beheersmaatregel]);
        if (riskRows.length === 0) riskRows.push(['Geen specifieke risico\'s', '-']);

        autoTable(doc, {
            startY: currentY,
            head: [['Risico', 'Maatregel']],
            body: riskRows,
            theme: 'striped',
            headStyles: { fillColor: [220, 38, 38] }, // Red header
            styles: { fontSize: 9 }
        });

        currentY = doc.lastAutoTable.finalY + 15;

        // --- LOTO ---
        if (data.lotoRequired || (data.specificLoto && data.specificLoto.length > 0)) {
            // Check page break
            if (currentY > 250) { doc.addPage(); currentY = 20; }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text("LOTO Isolatie Plan", 14, currentY);
            currentY += 5;

            const lotoRows = (data.specificLoto || []).map(l => [l.type, l.instruction, l.check]);
            if (lotoRows.length > 0) {
                autoTable(doc, {
                    startY: currentY,
                    head: [['Type', 'Instructie', 'Verificatie']],
                    body: lotoRows,
                    theme: 'grid',
                    headStyles: { fillColor: [234, 179, 8] }, // Yellow/Orange header
                    styles: { fontSize: 9 }
                });
                currentY = doc.lastAutoTable.finalY + 15;
            } else {
                doc.setFontSize(10);
                doc.setFont(undefined, 'normal');
                doc.text("Standaard LOTO procedure van toepassing.", 14, currentY);
                currentY += 10;
            }
        }

        // --- EXECUTION CHECKS ---
        if (data.executionChecklist) {
            // Check if we need a new page
            if (currentY > 250) {
                doc.addPage();
                currentY = 20;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text("Uitvoeringscontrole", 14, currentY);
            currentY += 5;

            // Convert checklist object to array
            const checks = [];
            // Assuming data.checks holds the labels mapped by index
            Object.keys(data.executionChecklist).forEach(key => {
                if (key.startsWith('mechanic_check_')) {
                    const idx = parseInt(key.split('_')[2]);
                    const label = data.checks && data.checks[idx] ? data.checks[idx] : `Check ${idx + 1}`;
                    const val = data.executionChecklist[key];
                    if (val === true) {
                        checks.push([label, 'JA']);
                    }
                }
            });

            if (checks.length > 0) {
                autoTable(doc, {
                    startY: currentY,
                    head: [['Checkpunt', 'Status']],
                    body: checks,
                    theme: 'plain',
                    headStyles: { fillColor: [71, 85, 105] },
                    styles: { fontSize: 9 }
                });
                currentY = doc.lastAutoTable.finalY + 20;
            }
        }

        // --- SIGNATURE BLOCK ---
        if (currentY > 230) {
            doc.addPage();
            currentY = 40;
        }

        doc.setLineWidth(0.5);
        doc.setDrawColor(150);

        // Box 1
        doc.rect(14, currentY, 80, 40);
        doc.setFontSize(10);
        doc.text("Handtekening Monteur:", 16, currentY + 6);
        doc.text(data.monteur1 || '', 16, currentY + 12);
        doc.setFontSize(8);
        doc.text("(Digitaal ondertekend in RaconWorks)", 16, currentY + 35);

        // Box 2
        doc.rect(110, currentY, 80, 40);
        doc.setFontSize(10);
        doc.text("Handtekening Werkverantwoordelijke:", 112, currentY + 6);
        doc.text(data.werkverantwoordelijke || '', 112, currentY + 12);

        // --- SAVE ---
        const filename = `Werkplan_${data.task || 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(filename);
    },

    generateAssetReport: (asset, history) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // --- HEADER ---
        doc.setFillColor(15, 23, 42); // Slate-900
        doc.rect(0, 0, pageWidth, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.text("Asset Historie & Status", 14, 25);

        doc.setFontSize(10);
        doc.text(`Datum: ${new Date().toLocaleDateString()}`, pageWidth - 14, 25, { align: 'right' });
        doc.text(`RaconWorks Asset Management`, pageWidth - 14, 32, { align: 'right' });

        let currentY = 50;

        // --- ASSET DETAILS ---
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(asset.instanceName || asset.assetId, 14, currentY);

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        currentY += 8;

        // Location context
        if (asset.locationString) {
            doc.text(`Locatie: ${asset.locationString}`, 14, currentY);
            currentY += 6;
        }

        // MJOP Data
        if (asset.mjop && Object.keys(asset.mjop).length > 0) {
            const mjop = asset.mjop;
            doc.text(`Bouwjaar: ${mjop.bouwjaar || 'Onbekend'}`, 14, currentY);
            doc.text(`Levensduur: ${mjop.levensduur || '-'} jaar`, 80, currentY);
            currentY += 6;
            doc.text(`Vervangingswaarde: € ${mjop.vervangingswaarde || '-'}`, 14, currentY);
            currentY += 10;
        } else {
            currentY += 10;
        }

        // --- HISTORY TABLE ---
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text("Logboek (Onderhoud & Inspecties)", 14, currentY);
        currentY += 6;

        const tableBody = history.map(p => {
            let statusLabel = p.status;
            if (p.status === 'completed') statusLabel = 'Afgerond';
            if (p.status === 'active') statusLabel = 'In Uitvoering';

            // Format Condition
            let condition = '-';
            if (p.conditionScore) condition = `NEN: ${p.conditionScore}`;

            return [
                new Date(p.updatedAt || p.createdAt).toLocaleDateString(),
                p.task || p.taskName || 'Onbekend',
                p.executor || p.monteur1 || '-',
                condition,
                statusLabel
            ];
        });

        if (tableBody.length === 0) {
            doc.setFont(undefined, 'italic');
            doc.setFontSize(10);
            doc.text("Geen historie gevonden.", 14, currentY + 5);
        } else {
            autoTable(doc, {
                startY: currentY,
                head: [['Datum', 'Taak', 'Uitvoerder', 'Conditie', 'Status']],
                body: tableBody,
                theme: 'striped',
                headStyles: { fillColor: [59, 130, 246] }, // Blue
                styles: { fontSize: 9 }
            });
        }

        // Save
        const safeName = (asset.instanceName || 'asset').replace(/[^a-z0-9]/gi, '_');
        doc.save(`Logboek_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    },

    generateAssetListReport: (title, context, assets) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // --- HEADER ---
        doc.setFillColor(30, 41, 59); // Slate-800
        doc.rect(0, 0, pageWidth, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.text(title, 14, 25);

        doc.setFontSize(10);
        doc.text(`Datum: ${new Date().toLocaleDateString()}`, pageWidth - 14, 25, { align: 'right' });
        doc.text(`RaconWorks Asset list`, pageWidth - 14, 32, { align: 'right' });

        let currentY = 50;

        // --- CONTEXT ---
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Locatie Context:', 14, currentY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        currentY += 6;
        if (context.location) doc.text(`Locatie: ${context.location}`, 14, currentY);
        if (context.hall) doc.text(`Hal: ${context.hall}`, 60, currentY);
        if (context.line) doc.text(`Lijn: ${context.line}`, 100, currentY);
        if (context.subline) doc.text(`Deellijn: ${context.subline}`, 140, currentY);

        currentY += 15;


        // --- TABLE ---
        const tableBody = assets.map(a => {
            // Predictive Logic
            const currentYear = new Date().getFullYear();
            const bouwjaar = a.bouwjaar ? parseInt(a.bouwjaar) : null;
            const levensduur = a.levensduur ? parseInt(a.levensduur) : null;
            let theoreticalEnd = (bouwjaar && levensduur) ? (bouwjaar + levensduur) : null;

            let prognose = theoreticalEnd ? theoreticalEnd.toString() : '-';
            let condition = a.latestCondition ? a.latestCondition.toString() : '-';

            // Apply NEN 2767-style prediction
            if (a.latestCondition) {
                const score = parseInt(a.latestCondition);
                if (score >= 5) {
                    // Critical: Replace ASAP
                    prognose = `${currentYear} (Urgent!)`;
                } else if (score === 4) {
                    // Poor: Replace within 2 years
                    const proposed = currentYear + 2;
                    if (theoreticalEnd && proposed < theoreticalEnd) {
                        prognose = `${proposed} (Vervroegd)`;
                    } else if (!theoreticalEnd) {
                        prognose = `${proposed}`;
                    }
                }
            }

            return [
                a.name,
                a.type,
                a.bouwjaar || '-',
                a.levensduur ? `${a.levensduur} jr` : '-',
                condition,
                prognose,
                a.vervangingswaarde ? `€ ${a.vervangingswaarde}` : '-'
            ];
        });

        autoTable(doc, {
            startY: currentY,
            head: [['Naam', 'Type', 'Bouwjaar', 'Levensduur', 'Conditie', 'Prognose', 'Vervangingswaarde']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [71, 85, 105] },
            styles: { fontSize: 10 },
            columnStyles: {
                0: { fontStyle: 'bold' }, // Name
                5: { fontStyle: 'bold', textColor: [200, 0, 0] } // Prognose (Highlight Red-ish for visibility, logic can be refined in didParseCell)
            },
            didParseCell: function (data) {
                if (data.section === 'body' && data.column.index === 5) {
                    // Dynamically text color for Urgent items
                    if (data.cell.text[0].includes('Urgent')) {
                        data.cell.styles.textColor = [220, 38, 38]; // Red
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
                if (data.section === 'body' && data.column.index === 4) {
                    // Condition formatting
                    const val = parseInt(data.cell.text[0]);
                    if (val >= 5) data.cell.styles.textColor = [220, 38, 38];
                    else if (val === 4) data.cell.styles.textColor = [217, 119, 6];
                }
            }
        });

        // --- TOTALS ---
        let totalValue = 0;
        assets.forEach(a => {
            if (a.vervangingswaarde) totalValue += parseFloat(a.vervangingswaarde);
        });

        if (totalValue > 0) {
            let finalY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(`Totaal Vervangingswaarde: € ${totalValue.toLocaleString('nl-NL')}`, 14, finalY);
        }

        // Save
        const safeName = title.replace(/[^a-z0-9]/gi, '_');
        doc.save(`${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    }
};
